// Karacal widget loader: injected only on explicit request (consent + CTA click).
const LOAD_TIMEOUT_MS = 10000;
const WIDGET_TAG = "mimir-chat-widget";

let loadPromise = null;
let loaded = false;
const injected = [];

function captureWidgetShadowRoot() {
    if (window.__ediShadowPatched) return;
    const original = Element.prototype.attachShadow;
    Element.prototype.attachShadow = function patchedAttachShadow(init) {
        const root = original.call(this, init);
        if (this.tagName && this.tagName.toLowerCase() === WIDGET_TAG) window.__ediWidget = { host: this, root };
        return root;
    };
    window.__ediShadowPatched = true;
}

export function parseEmbedCode(code) {
    const doc = new DOMParser().parseFromString(code || "", "text/html");
    return [...doc.querySelectorAll("script")]
        .map((s) => [...s.attributes].map((a) => [a.name, a.value]))
        .filter((attrs) => attrs.some(([name]) => name === "src"));
}

function getLauncher() {
    const root = window.__ediWidget?.root;
    return root ? root.querySelector("#launcher") || root.querySelector("button") : null;
}

function waitForLauncher(deadline) {
    return new Promise((resolve, reject) => {
        const tick = () => {
            const button = getLauncher();
            if (button) return resolve(button);
            if (Date.now() > deadline) return reject(new Error("timeout"));
            return setTimeout(tick, 100);
        };
        tick();
    });
}

function removeInjectedScripts() {
    injected.splice(0).forEach((el) => el.remove());
}

export const isWidgetLoaded = () => loaded;

export function loadWidget(code) {
    if (loadPromise) return loadPromise;
    const scripts = parseEmbedCode(code);
    if (!scripts.length) return Promise.reject(new Error("no-code"));
    captureWidgetShadowRoot();
    const deadline = Date.now() + LOAD_TIMEOUT_MS;
    loadPromise = new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("timeout")), LOAD_TIMEOUT_MS);
        const loads = scripts.map(
            (attrs) =>
                new Promise((res, rej) => {
                    const el = document.createElement("script");
                    attrs.forEach(([name, value]) => el.setAttribute(name, value));
                    el.async = true;
                    el.setAttribute("data-edi-widget", "true");
                    el.onload = res;
                    el.onerror = () => rej(new Error("load-error"));
                    injected.push(el);
                    document.body.appendChild(el);
                }),
        );
        Promise.all(loads)
            .then(() => waitForLauncher(deadline))
            .then((button) => {
                clearTimeout(timer);
                loaded = true;
                resolve(button);
            })
            .catch((err) => {
                clearTimeout(timer);
                reject(err);
            });
    });
    loadPromise.catch(() => {
        removeInjectedScripts();
        loadPromise = null;
    });
    return loadPromise;
}

export function openChat() {
    const button = getLauncher();
    if (!button) throw new Error("launcher-missing");
    if (button.getAttribute("aria-expanded") !== "true") button.click();
}

// The widget re-attaches its host element to <html>, so a full reload is the only clean removal.
export function removeWidgetAndReload(url) {
    document.querySelectorAll(WIDGET_TAG).forEach((el) => el.style.setProperty("display", "none", "important"));
    removeInjectedScripts();
    if (url) window.location.assign(url);
    else window.location.reload();
}
